import {
    Box,
    Button,
    Center,
    Flex,
    Group,
    Image,
    Loader,
    Modal,
    Overlay,
    Paper,
    ScrollArea,
    Select,
    Stack,
    Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconPhoto, IconPlayerPlay } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState, useEffect, useMemo } from 'react';

import type { CreativeMediaItem, AssetType } from '../data/media';
import { useCreativeMedia, getAssetType } from '../data/media';
import * as remote from '../data/remote-data';
import { plural } from '../lib/utils';

import { Table } from './Table';
import { ThumbnailPreview } from './ThumbnailPreview';

type CreativeSelectorProps = {
    gameId: string;
    selectedMediaId?: string;
    selectedMediaItems?: CreativeMediaItem[];
    onSelect: (mediaItems: CreativeMediaItem[]) => void;
    onCancel?: () => void;
};

interface CreativeFilters {
    assetType: 'all' | AssetType;
    fromDate: string;
    toDate: string;
}

export const CreativeSelector = ({
    gameId,
    selectedMediaId,
    selectedMediaItems,
    onSelect,
    onCancel,
}: CreativeSelectorProps) => {
    const media = useCreativeMedia(gameId);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
    const [filters, setFilters] = useState<CreativeFilters>({
        assetType: 'all',
        fromDate: '',
        toDate: '',
    });

    // Auto-load media data when modal opens and data hasn't been loaded yet
    useEffect(() => {
        if (isOpen && remote.isNotAsked(media.data)) {
            if (!gameId) {
                return;
            }
            void media.load();
        }
    }, [isOpen, media.data, media.load, gameId]);

    // Pre-select the currently selected creative when modal opens
    useEffect(() => {
        if (isOpen && selectedMediaId) {
            setSelectedIds(new Set([selectedMediaId]));
        }
    }, [isOpen, selectedMediaId]);

    // Apply filters to media data
    const filteredMedia = useMemo(() => {
        let items = remote.withDefault(media.data, []);

        // Filter by asset type
        if (filters.assetType !== 'all') {
            items = items.filter((item) => getAssetType(item) === filters.assetType);
        }

        // Filter by date range
        if (filters.fromDate) {
            items = items.filter((item) => {
                const itemDate = dayjs(item.uploadedAt).startOf('day');
                const fromDate = dayjs(filters.fromDate).startOf('day');
                return itemDate.valueOf() >= fromDate.valueOf();
            });
        }

        if (filters.toDate) {
            items = items.filter((item) => {
                const itemDate = dayjs(item.uploadedAt).endOf('day');
                const toDate = dayjs(filters.toDate).endOf('day');
                return itemDate.valueOf() <= toDate.valueOf();
            });
        }

        return items;
    }, [media.data, filters]);

    const handleSubmit = () => {
        if (selectedIds.size === 0) {
            notifications.show({
                title: 'Selection required',
                message: 'Please select at least one creative.',
                color: 'red',
            });
            return;
        }

        const selected = Array.from(selectedIds)
            .map((id) => filteredMedia.find((item) => item.mediaId === id))
            .filter((item): item is CreativeMediaItem => item !== undefined);

        if (selected.length > 0) {
            onSelect(selected);
            setIsOpen(false);
            setSelectedIds(new Set());
        }
    };

    const handleCancel = () => {
        setIsOpen(false);
        setSelectedIds(new Set());
        if (onCancel) {
            onCancel();
        }
    };

    const handleClearFilters = () => {
        setFilters({
            assetType: 'all',
            fromDate: '',
            toDate: '',
        });
    };

    const hasActiveFilters = filters.assetType !== 'all' || filters.fromDate !== '' || filters.toDate !== '';

    const selectedMediaItem = selectedMediaId
        ? remote.withDefault(media.data, []).find((item) => item.mediaId === selectedMediaId)
        : undefined;

    return (
        <Box>
            {/* Selected Creative Display */}
            {selectedMediaItem ? (
                <Paper
                    withBorder
                    p="sm"
                    bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                    radius="sm"
                >
                    <Group gap="sm">
                        <Box style={{ flexShrink: 0 }}>
                            <Paper
                                withBorder
                                radius="sm"
                                w={48}
                                h={48}
                                bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))"
                                style={{ overflow: 'hidden' }}
                            >
                                {selectedMediaItem.mimeType.startsWith('image/') ? (
                                    <Image
                                        src={selectedMediaItem.url}
                                        alt={selectedMediaItem.name}
                                        w="100%"
                                        h="100%"
                                        fit="cover"
                                    />
                                ) : selectedMediaItem.mimeType.startsWith('video/') ? (
                                    <Box pos="relative" w="100%" h="100%">
                                        <video
                                            src={selectedMediaItem.url}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            muted
                                            preload="metadata"
                                        />
                                        <Overlay backgroundOpacity={0.2}>
                                            <Center h="100%">
                                                <Box
                                                    c="dark.8"
                                                    style={{
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        borderRadius: '50%',
                                                        padding: 4,
                                                    }}
                                                >
                                                    <IconPlayerPlay size={12} />
                                                </Box>
                                            </Center>
                                        </Overlay>
                                    </Box>
                                ) : (
                                    <Center w="100%" h="100%" c="dimmed">
                                        <IconPhoto size={24} />
                                    </Center>
                                )}
                            </Paper>
                        </Box>
                        <Box flex={1} miw={0}>
                            <Text fw={500} truncate>
                                {selectedMediaItem.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {selectedMediaItem.width} x {selectedMediaItem.height} &bull;{' '}
                                {selectedMediaItem.mimeType}
                            </Text>
                        </Box>
                        <Button variant="subtle" onClick={() => setIsOpen(true)}>
                            Change
                        </Button>
                    </Group>
                </Paper>
            ) : selectedMediaItems && selectedMediaItems.length > 0 ? (
                <Paper
                    component="button"
                    type="button"
                    onClick={() => setIsOpen(true)}
                    p="lg"
                    w="100%"
                    radius="sm"
                    bd="light-dark(2px dashed var(--mantine-color-gray-3), 2px dashed var(--mantine-color-dark-4))"
                    style={{
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        cursor: 'pointer',
                        font: 'inherit',
                    }}
                >
                    <Group gap="xs" justify="center" style={{ marginBottom: 8 }}>
                        <Flex style={{ marginLeft: -4 }}>
                            {selectedMediaItems.slice(0, 5).map((item, idx) => (
                                <Paper
                                    key={item.mediaId}
                                    radius="sm"
                                    shadow="xs"
                                    pos="relative"
                                    w={48}
                                    h={48}
                                    bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))"
                                    bd="light-dark(2px solid var(--mantine-color-white), 2px solid var(--mantine-color-dark-4))"
                                    style={{
                                        overflow: 'hidden',
                                        zIndex: 5 - idx,
                                    }}
                                >
                                    {item.mimeType?.startsWith('image/') ? (
                                        <Image src={item.url} alt={item.name} w="100%" h="100%" fit="cover" />
                                    ) : item.mimeType?.startsWith('video/') ? (
                                        <Box pos="relative" w="100%" h="100%">
                                            <video
                                                src={item.url}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                muted
                                                preload="metadata"
                                            />
                                            <Overlay backgroundOpacity={0.2}>
                                                <Center h="100%">
                                                    <Box
                                                        c="dark.8"
                                                        style={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                            borderRadius: '50%',
                                                            padding: 2,
                                                        }}
                                                    >
                                                        <IconPlayerPlay size={12} />
                                                    </Box>
                                                </Center>
                                            </Overlay>
                                        </Box>
                                    ) : (
                                        <Center w="100%" h="100%" c="dimmed">
                                            <IconPhoto size={20} />
                                        </Center>
                                    )}
                                </Paper>
                            ))}
                        </Flex>
                        {selectedMediaItems.length > 5 && (
                            <Paper
                                w={48}
                                h={48}
                                bg="light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))"
                                radius="sm"
                                shadow="xs"
                                bd="light-dark(2px solid var(--mantine-color-white), 2px solid var(--mantine-color-dark-4))"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <Text size="xs" fw={500} c="dimmed">
                                    +{selectedMediaItems.length - 5}
                                </Text>
                            </Paper>
                        )}
                    </Group>
                    <Text fw={500} display="block">
                        {selectedMediaItems.length} {plural(selectedMediaItems.length, 'Creative', 'Creatives')}{' '}
                        Selected
                    </Text>
                    <Text size="xs" c="dimmed" display="block">
                        Click to change selection
                    </Text>
                </Paper>
            ) : (
                <Paper
                    component="button"
                    type="button"
                    onClick={() => setIsOpen(true)}
                    p="lg"
                    w="100%"
                    radius="sm"
                    bd="light-dark(2px dashed var(--mantine-color-gray-3), 2px dashed var(--mantine-color-dark-4))"
                    style={{
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        cursor: 'pointer',
                        font: 'inherit',
                    }}
                >
                    <Center c="dimmed">
                        <IconPhoto size={48} />
                    </Center>
                    <Text fw={500} display="block" mt="xs">
                        Select Creative
                    </Text>
                    <Text size="xs" c="dimmed" display="block" mt={4}>
                        Choose from your creative repository
                    </Text>
                </Paper>
            )}

            {/* Creative Selection Modal */}
            <Modal
                opened={isOpen}
                onClose={handleCancel}
                size="90%"
                title="Select Creative Media"
                styles={{
                    content: { maxHeight: '90vh' },
                }}
            >
                <Stack gap="md">
                    <Text c="dimmed">Choose a creative from your repository to use for this ad.</Text>

                    {/* Filter Controls */}
                    <Paper
                        withBorder
                        p="md"
                        bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                        radius="sm"
                    >
                        <Group gap="sm" align="flex-end" wrap="wrap">
                            <Box flex={1} miw={150}>
                                <Select
                                    label="Asset Type"
                                    value={filters.assetType}
                                    onChange={(value) => {
                                        if (value)
                                            setFilters({
                                                ...filters,
                                                assetType: value as CreativeFilters['assetType'],
                                            });
                                    }}
                                    data={[
                                        { value: 'all', label: 'All Types' },
                                        { value: 'image', label: 'Image' },
                                        { value: 'video', label: 'Video' },
                                        { value: 'animated-image', label: 'Animated Image' },
                                    ]}
                                />
                            </Box>
                            <Box flex={1} miw={150}>
                                <DatePickerInput
                                    label="From Date"
                                    value={filters.fromDate ? dayjs(filters.fromDate).toDate() : null}
                                    onChange={(date) => {
                                        if (date) {
                                            setFilters({ ...filters, fromDate: dayjs(date).format('YYYY-MM-DD') });
                                        } else {
                                            setFilters({ ...filters, fromDate: '' });
                                        }
                                    }}
                                    clearable
                                />
                            </Box>
                            <Box flex={1} miw={150}>
                                <DatePickerInput
                                    label="To Date"
                                    value={filters.toDate ? dayjs(filters.toDate).toDate() : null}
                                    onChange={(date) => {
                                        if (date) {
                                            setFilters({ ...filters, toDate: dayjs(date).format('YYYY-MM-DD') });
                                        } else {
                                            setFilters({ ...filters, toDate: '' });
                                        }
                                    }}
                                    clearable
                                />
                            </Box>
                            <Button variant="outline" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                                Clear Filters
                            </Button>
                        </Group>
                    </Paper>

                    {/* Media List */}
                    {remote.matchAll(media.data, {
                        NotAsked: () => (
                            <Box ta="center" py="xl">
                                <Button onClick={media.load} leftSection={<IconPhoto size={16} />}>
                                    Load Creative Media
                                </Button>
                            </Box>
                        ),
                        Loading: () => (
                            <Center py="xl">
                                <Loader />
                                <Text c="dimmed" ml="sm">
                                    Loading creatives...
                                </Text>
                            </Center>
                        ),
                        Failure: (error) => (
                            <Box ta="center" py="xl">
                                <Center c="red.4">
                                    <IconPhoto size={48} />
                                </Center>
                                <Text fw={500} mt="xs">
                                    Failed to load creatives
                                </Text>
                                <Text size="xs" c="red">
                                    {error}
                                </Text>
                                {!gameId && (
                                    <Text size="xs" c="red" display="block" mt={4}>
                                        Error: gameId is missing or invalid
                                    </Text>
                                )}
                                <Button color="red" onClick={media.load} mt="md">
                                    Try again
                                </Button>
                            </Box>
                        ),
                        Success: () => {
                            if (filteredMedia.length === 0) {
                                const hasFilters = hasActiveFilters;
                                return (
                                    <Box ta="center" py="xl">
                                        <Center c="dimmed">
                                            <IconPhoto size={48} />
                                        </Center>
                                        <Text fw={500} mt="xs">
                                            {hasFilters ? 'No creatives match your filters' : 'No creative media found'}
                                        </Text>
                                        <Text size="xs" c="dimmed" mt={4}>
                                            {hasFilters
                                                ? 'Try adjusting your filter criteria or clear all filters.'
                                                : 'Upload some creatives to get started.'}
                                        </Text>
                                        {hasFilters && (
                                            <Box mt="md">
                                                <Button variant="outline" onClick={handleClearFilters}>
                                                    Clear All Filters
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            }

                            return (
                                <ScrollArea type="auto">
                                    <Table
                                        data={filteredMedia}
                                        id={(row) => row.mediaId}
                                        defaultSortColumn="Upload Date"
                                        pagination={{
                                            pageSize: 10,
                                            showPagination: true,
                                        }}
                                        selection={{
                                            selectedIds,
                                            onSelectionChange: setSelectedIds,
                                        }}
                                        columns={[
                                            {
                                                name: 'Preview',
                                                alt: 'Media preview',
                                                width: 80,
                                                render: (row) => {
                                                    const isVideo = row.mimeType.startsWith('video/');
                                                    return (
                                                        <ThumbnailPreview
                                                            url={row.url}
                                                            isVideo={isVideo}
                                                            alt={row.name}
                                                        />
                                                    );
                                                },
                                            },
                                            {
                                                name: 'Name',
                                                alt: 'Generated name from marketing fields',
                                                render: (row) => row.name,
                                                sort: (a, b) => a.name.localeCompare(b.name),
                                            },
                                            {
                                                name: 'Upload Date',
                                                alt: 'Date when the media was uploaded',
                                                width: 200,
                                                render: (row) => dayjs(row.uploadedAt).format('MMM D, YYYY h:mm A'),
                                                sort: (a, b) => a.uploadedAt - b.uploadedAt,
                                                defaultDirection: 'desc' as const,
                                            },
                                        ]}
                                    />
                                </ScrollArea>
                            );
                        },
                    })}
                </Stack>

                <Group justify="flex-end" gap="sm" mt="md">
                    <Button variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={selectedIds.size === 0}>
                        Select {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
};
